import { computed, onBeforeUnmount, ref, watch, type Ref } from "vue";
import { useStore } from "vuex";
import placeholderImg from "~/assets/img/placeholder.png";
import { hasRenditions, renditionUrl } from "~/utils/cdn";
import {
  productMediaJpegSrc,
  productMediaRungSrc,
} from "~/core/utils/productMedia";
import {
  hasProductSpecial,
  resolveProductPrice,
} from "../../../../../utils/productPrice";

type ViewportRefs = {
  isPhoneViewport: Ref<boolean>;
  isTabletViewport: Ref<boolean>;
};

export function useProductStickyBar(
  product: Ref<Record<string, any> | null>,
  loading: Ref<boolean>,
  viewport: ViewportRefs
) {
  // The sticky-buy thumb is under 80 CSS px; this rung stays sharp at 2x+.
  const STICKY_MEDIA_MIN_WIDTH = 160;
  const mainBuyEl = ref<HTMLElement | null>(null);
  // Starts true so the bar cannot flash at load on the 1024-1279px two-column
  // layout, where Add to Cart is already on screen; the observer's first
  // callback corrects this before the shopper can act.
  const mainBuyVisible = ref(true);
  let buyObserver: IntersectionObserver | null = null;

  const store = useStore();
  const isCartOpen = computed(() => store.getters["vuefront/sidebarCart"]);

  const showStickyBar = computed(
    () =>
      !loading.value &&
      !!product.value?.id &&
      !product.value?.__productPreview &&
      (viewport.isPhoneViewport.value || viewport.isTabletViewport.value) &&
      !!mainBuyEl.value &&
      !mainBuyVisible.value &&
      !isCartOpen.value
  );

  const stickyDisplayPrice = computed(() =>
    resolveProductPrice(product.value?.special, product.value?.price)
  );
  const stickyHasSpecial = computed(() =>
    hasProductSpecial(product.value?.special, product.value?.price)
  );

  const resolveStickyThumb = (source: unknown, media?: unknown) => {
    // The main photo's own `media`: a small AVIF rung. Its error steps to
    // the JPEG, then to the legacy photo, then to the placeholder.
    const mediaSrc = productMediaRungSrc(media, STICKY_MEDIA_MIN_WIDTH);
    if (mediaSrc) return mediaSrc;
    const imageUrl = String(source ?? "").trim();
    if (!imageUrl) return placeholderImg;
    // A PNG/JPEG from the catalog does not imply that AVIF renditions exist.
    if (!hasRenditions(imageUrl)) return imageUrl;
    return (
      renditionUrl(imageUrl, 600, { ratio: 1, format: "avif" }) || imageUrl
    );
  };

  // Reuse the gallery's cached rendition only for a catalog AVIF source.
  const stickyThumbSrc = ref(
    resolveStickyThumb(product.value?.image, product.value?.media)
  );
  watch(
    () =>
      `${product.value?.id ?? ""}|${
        product.value?.image ?? ""
      }|${productMediaRungSrc(product.value?.media, STICKY_MEDIA_MIN_WIDTH)}`,
    () => {
      stickyThumbSrc.value = resolveStickyThumb(
        product.value?.image,
        product.value?.media
      );
    }
  );

  const onStickyThumbError = () => {
    const mediaJpeg = productMediaJpegSrc(product.value?.media);
    if (
      mediaJpeg &&
      stickyThumbSrc.value !== mediaJpeg &&
      stickyThumbSrc.value ===
        productMediaRungSrc(product.value?.media, STICKY_MEDIA_MIN_WIDTH)
    ) {
      stickyThumbSrc.value = mediaJpeg;
      return;
    }
    const original = String(product.value?.image ?? "").trim();
    if (
      original &&
      stickyThumbSrc.value !== original &&
      stickyThumbSrc.value !== placeholderImg
    ) {
      stickyThumbSrc.value = original;
      return;
    }
    if (stickyThumbSrc.value !== placeholderImg) {
      stickyThumbSrc.value = placeholderImg;
    }
  };

  const teardownBuyObserver = () => {
    if (buyObserver) {
      buyObserver.disconnect();
      buyObserver = null;
    }
  };

  const setupBuyObserver = () => {
    teardownBuyObserver();
    if (typeof IntersectionObserver === "undefined" || !mainBuyEl.value) {
      // Without an observer the main button's position is unknown; keep the
      // bar up rather than leaving the gallery with no reachable CTA.
      mainBuyVisible.value = false;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // A disconnected observer can still deliver queued entries; they
        // describe a button that is no longer the one on the page.
        if (observer !== buyObserver) return;
        // Entries arrive oldest first, so a batch that saw the button enter
        // and leave again ends with its current state.
        const entry = entries[entries.length - 1];
        // The bar stands in for the main Add to Cart whenever that button is
        // off-screen — while the shopper is still on the photos above it as
        // well as after it has scrolled past the top — and steps aside while
        // the real button is on screen so there is never a duplicate CTA.
        if (entry) mainBuyVisible.value = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    buyObserver = observer;
    observer.observe(mainBuyEl.value);
  };

  const setMainBuyRef = (element: Element | any | null) => {
    if (element instanceof HTMLElement) {
      mainBuyEl.value = element;
      setupBuyObserver();
      return;
    }

    mainBuyEl.value = null;
    // Reset, so a remounted button gets the same no-flash start.
    mainBuyVisible.value = true;
    teardownBuyObserver();
  };

  // ── Reserve the bar's own height at the end of the document ──────────
  // The bar is fixed to the viewport bottom and teleported to <body>, so it
  // covers whatever the page ends with — on the PDP that is the footer's
  // Privacy / Terms / "Your privacy choices" row (WCAG 2.2 SC 2.4.11). The
  // height is measured, never hard-coded: it changes with the viewport (the
  // 64px thumb is dropped below 361px), with the font that actually loads,
  // and with env(safe-area-inset-bottom) at runtime.
  const BAR_CLASS = "vf-has-pdp-sticky-buy";
  const BAR_VAR = "--vf-pdp-sticky-buy-height";
  // The window that follows a jump to the end: at most 500ms and at most 40
  // callbacks, whichever ends first. Both bounds are needed — the frame count
  // alone would stretch on a throttled tab, the clock alone would keep a
  // starved page polling.
  const PIN_MS = 500;
  const PIN_FRAMES = 40;
  let barEl: HTMLElement | null = null;
  let barResize: ResizeObserver | null = null;
  let pinFrame: number | null = null;
  let releasePinGuard: (() => void) | null = null;
  let reserved = false;
  let reservedHeight = 0;

  const hasDom = () => typeof document !== "undefined" && !!document.body;

  const now = () =>
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();

  const scrollerEl = (): any =>
    document.scrollingElement || document.documentElement;

  const cancelPin = () => {
    if (pinFrame !== null && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(pinFrame);
    }
    pinFrame = null;
    const release = releasePinGuard;
    releasePinGuard = null;
    if (release) release();
  };

  const clearBarOffset = () => {
    cancelPin();
    reserved = false;
    reservedHeight = 0;
    if (!hasDom()) return;
    document.body.classList.remove(BAR_CLASS);
    document.body.style.removeProperty(BAR_VAR);
  };

  // Follows the house rule of scrollRestoreGuard: a programmatic scroll gives
  // way the moment the shopper scrolls for themselves. Keys are deliberately
  // not watched — End is the very gesture this correction exists to serve.
  const armPinGuard = () => {
    if (typeof window === "undefined" || releasePinGuard) return;
    const stop = () => cancelPin();
    const opts: any = { capture: true, passive: true };
    window.addEventListener("wheel", stop, opts);
    window.addEventListener("touchstart", stop, opts);
    window.addEventListener("touchmove", stop, opts);
    window.addEventListener("pointerdown", stop, opts);
    releasePinGuard = () => {
      window.removeEventListener("wheel", stop, true);
      window.removeEventListener("touchstart", stop, true);
      window.removeEventListener("touchmove", stop, true);
      window.removeEventListener("pointerdown", stop, true);
    };
  };

  // The reservation is space added at the END of the document, so a jump to
  // the end — the End key, a scrollbar drag, a fling that has already left
  // the finger — is a problem: the browser commits to the maximum it can
  // see, the bar only arrives after that, and the space then opens below the
  // viewport with the footer row still under the bar. (A fling still under
  // the finger gives way instead: its touchmove cancels the window, by
  // design.) Only the call that FIRST publishes the reservation
  // may do this: there, a gap no larger than the bar is by definition the
  // space just added, so closing it gives back nothing but padding. On a
  // later re-measure the same test would mean only "the shopper is near the
  // end", and pulling them down from a resting position is not ours to do.
  // The window exists because a keyboard scroll can land after the observer
  // has already fired, and it keeps checking rather than writing once, since
  // a scroll still in flight would otherwise overwrite the correction.
  const settleToBottom = (deadline: number, framesLeft: number) => {
    const scroller = scrollerEl();
    if (!scroller || typeof scroller.scrollTop !== "number") {
      cancelPin();
      return;
    }

    // Read, not captured at arm time: a bar that grew mid-window — a late
    // font, a name that re-wrapped — moved the boundary with it.
    const gap =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (gap > 0 && gap <= reservedHeight + 1) {
      scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight;
      // Corrected once and done. Holding the page at the bottom for the rest
      // of the window would undo an Arrow/PageUp key pressed right after End.
      cancelPin();
      return;
    }

    if (
      framesLeft <= 0 ||
      now() > deadline ||
      typeof requestAnimationFrame === "undefined"
    ) {
      cancelPin();
      return;
    }
    pinFrame = requestAnimationFrame(() => {
      pinFrame = null;
      settleToBottom(deadline, framesLeft - 1);
    });
  };

  const syncBarOffset = () => {
    if (!hasDom()) return;
    // A host node in a non-DOM test, and a display:none bar at >=1280px,
    // both report no usable height — there is then nothing to reserve.
    const height = Number(barEl?.offsetHeight);
    if (!(height > 0)) {
      clearBarOffset();
      return;
    }

    // Only the call that ADDS the reservation may correct the scroll. Later
    // deliveries (re-measures, a replaced bar node) must neither cancel the
    // window that one opened nor open one of their own.
    const wasReserved = reserved;

    const scroller = scrollerEl();
    // Taken before the write, so it describes where the shopper already was.
    const atEnd =
      !!scroller &&
      typeof scroller.scrollTop === "number" &&
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= 1;

    reservedHeight = Math.round(height);
    document.body.style.setProperty(BAR_VAR, `${reservedHeight}px`);
    document.body.classList.add(BAR_CLASS);
    reserved = true;

    if (wasReserved) {
      // A re-measure follows only a shopper who is already AT the end. A bar
      // that grows there — iOS collapsing its toolbar changes the safe-area
      // inset — would otherwise slide the footer row back underneath it.
      // Anyone resting short of the end is left exactly where they are.
      if (atEnd) {
        scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight;
      }
      return;
    }
    cancelPin();
    armPinGuard();
    settleToBottom(now() + PIN_MS, PIN_FRAMES);
  };

  const teardownBarResize = () => {
    if (barResize) {
      barResize.disconnect();
      barResize = null;
    }
  };

  const setStickyBarRef = (element: Element | any | null) => {
    if (element instanceof HTMLElement) {
      // Vue calls a function ref on every re-render of the bar. The same node
      // is already observed, and the observer reports any real size change.
      if (element === barEl && barResize) return;
      teardownBarResize();
      barEl = element;
      // The bar grows when the product name wraps or a late font lands.
      // Observed as a border box, because the published measurement is
      // offsetHeight and the safe-area inset arrives as padding — a
      // content-box observation never sees it change.
      if (typeof ResizeObserver !== "undefined") {
        // The observer's own first delivery (after layout, before paint)
        // makes the first measurement. Measuring here, inside the render
        // that mounts the bar, forced a synchronous layout of the whole page
        // (perf audit 2026-10-01: 70-111 ms on a throttled phone).
        barResize = new ResizeObserver(() => syncBarOffset());
        barResize.observe(element, { box: "border-box" });
        return;
      }
      syncBarOffset();
      return;
    }

    // Vue nulls a template ref at the start of unmount, ahead of the 240ms
    // leave transition, so the reservation goes as the bar starts leaving.
    teardownBarResize();
    barEl = null;
    clearBarOffset();
  };

  onBeforeUnmount(() => {
    teardownBuyObserver();
    teardownBarResize();
    clearBarOffset();
  });

  return {
    setMainBuyRef,
    setStickyBarRef,
    showStickyBar,
    stickyDisplayPrice,
    stickyHasSpecial,
    stickyThumbSrc,
    onStickyThumbError,
  };
}
